import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getProjetosCompleto } from "../../api/projetos.api";
import { criarTarefa, atualizarTarefa, deletarTarefa, getComentarios, adicionarComentario, deletarComentario, getHistorico } from "../../api/tarefas.api";
import { getUser } from "../../api/auth.api";
import { getGtdTickets } from "../../api/gtd.api";
import GtdQuickAssign from "../../components/GtdQuickAssign";

/* ─── Constantes ──────────────────────────────────────────────────── */

const COLUNAS = [
  { key: "Nao Iniciada", label: "Não Iniciada", cor: "#64748b", bg: "rgba(100,116,139,0.10)" },
  { key: "Em Andamento", label: "Em Andamento", cor: "#60a5fa", bg: "rgba(96,165,250,0.10)"  },
  { key: "Bloqueada",    label: "Bloqueada",    cor: "#f87171", bg: "rgba(248,113,113,0.10)" },
  { key: "Concluida",    label: "Concluída",    cor: "#4ade80", bg: "rgba(74,222,128,0.10)"  },
];

const PROXIMAS_ACOES = {
  "Nao Iniciada": [{ label: "Iniciar",  key: "Em Andamento", cor: "#60a5fa" }],
  "Em Andamento": [
    { label: "Concluir", key: "Concluida", cor: "#4ade80" },
    { label: "Bloquear", key: "Bloqueada", cor: "#f87171" },
  ],
  "Bloqueada":    [{ label: "Retomar",  key: "Em Andamento", cor: "#60a5fa" }],
  "Concluida":    [],
};

/* ─── Helpers ─────────────────────────────────────────────────────── */

function fmtDate(str) {
  if (!str) return "—";
  const [y, m, d] = str.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function isOverdue(str, status) {
  if (!str || status === "Concluida") return false;
  return new Date(str) < new Date(new Date().toDateString());
}

/* Cor da borda esquerda do card baseada na urgência do prazo */
function urgenciaCor(dataEntrega, status) {
  if (!dataEntrega || status === "Concluida") return "rgba(96,165,250,0.12)";
  const hoje = new Date(new Date().toDateString());
  const prazo = new Date(dataEntrega);
  const diff = Math.floor((prazo - hoje) / 864e5);
  if (diff < 0)  return "#f87171";
  if (diff <= 1) return "#fb923c";
  if (diff <= 7) return "#fbbf24";
  return "rgba(96,165,250,0.2)";
}

/* Label de urgência: "3 dias de atraso", "Vence hoje", "Vence amanhã" */
function urgenciaLabel(dataEntrega, status) {
  if (!dataEntrega || status === "Concluida") return null;
  const hoje = new Date(new Date().toDateString());
  const prazo = new Date(dataEntrega);
  const diff = Math.floor((prazo - hoje) / 864e5);
  if (diff < 0)  return `${Math.abs(diff)} dia${Math.abs(diff) !== 1 ? "s" : ""} de atraso`;
  if (diff === 0) return "Vence hoje";
  if (diff === 1) return "Vence amanhã";
  return null;
}

/* Avatar colorido com iniciais do responsável */
const AVATAR_COLORS = ["#60a5fa","#22d3ee","#a78bfa","#f472b6","#4ade80","#fb923c","#fbbf24"];
function Avatar({ nome }) {
  if (!nome) return null;
  const partes = nome.trim().split(" ");
  const initials = partes.length >= 2
    ? (partes[0][0] + partes[partes.length - 1][0]).toUpperCase()
    : nome[0].toUpperCase();
  const cor = AVATAR_COLORS[
    nome.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length
  ];
  return (
    <div style={{
      width: 22, height: 22, borderRadius: "50%",
      background: `${cor}22`, border: `1.5px solid ${cor}55`,
      color: cor, fontSize: 9, fontWeight: 800,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function emptyForm(id_ticket = "", status = "Nao Iniciada") {
  return {
    id_ticket,
    descritivo: "",
    responsavel: "",
    status,
    data_entrega: "",
    data_entrega_real: "",
    descritivo_restricao: "",
    responsavel_restricao: "",
    data_liberacao_restricao: "",
  };
}

/* ─── Página principal ────────────────────────────────────────────── */

const ACOMP_STORAGE = "tarefas_acomp_filtros";
function loadAcompFiltros() {
  try { const s = localStorage.getItem(ACOMP_STORAGE); return s ? JSON.parse(s) : {}; }
  catch { return {}; }
}

export default function TarefasAcompanhamento() {
  const [tickets,      setTickets]      = useState([]);
  const [tarefas,      setTarefas]      = useState([]);
  const [gtdMap,       setGtdMap]       = useState({});
  const [hierarquia,   setHierarquia]   = useState([]);
  const [painelAberto, setPainelAberto] = useState(true);
  const [loading,      setLoading]      = useState(true);
  const _f = loadAcompFiltros();
  const [filtroStatus,  setFiltroStatus]  = useState(_f.status  ?? null);
  const [filtroProjeto, setFiltroProjeto] = useState(_f.projeto ?? "");
  const [filtroTicket,  setFiltroTicket]  = useState(_f.ticket  ?? "");
  const [filtroResp,    setFiltroResp]    = useState(_f.resp    ?? "");
  const [modal,        setModal]        = useState(null);
  const [form,         setForm]         = useState(emptyForm());
  const [saving,       setSaving]       = useState(false);
  const [formErro,     setFormErro]     = useState("");
  const [countdown,    setCountdown]    = useState(60);
  const [dragOverCol,  setDragOverCol]  = useState(null);
  const [ordenacao,    setOrdenacao]    = useState(
    () => { try { return localStorage.getItem("tarefas_acomp_ordem") || ""; } catch { return ""; } }
  );
  const cntRef   = useRef(60);
  const isFirst  = useRef(true);
  const location = useLocation();

  useEffect(() => {
    const s = location.state;
    if (!s?.ts) return;
    if (s.ticket  !== undefined) setFiltroTicket(s.ticket);
    if (s.projeto !== undefined) setFiltroProjeto(s.projeto);
  }, [location.state]);

  /* ── Carregamento de dados ── */
  const load = useCallback(async (bust = false) => {
    try {
      const { projetos: rows, tarefas: allTarefas } = await getProjetosCompleto(bust, 2);
      const projetoMap = {};
      (rows || []).forEach(r => {
        if (r.id_projeto && !projetoMap[r.id_projeto]) {
          projetoMap[r.id_projeto] = {
            id:             r.id_projeto,
            descricao:      r.descricao,
            nivel:          r.nivel,
            id_projeto_pai: r.id_projeto_pai,
            importancia:    r.importancia || 0,
          };
        }
      });

      const byImportancia = (a, b) => (b.importancia || 0) - (a.importancia || 0);
      const pais = Object.values(projetoMap).filter(p => !p.id_projeto_pai).sort(byImportancia);
      setHierarquia(pais.map(pai => ({
        ...pai,
        filhos: Object.values(projetoMap).filter(p => p.id_projeto_pai === pai.id).sort(byImportancia),
      })));

      const ticketMap = {};
      (rows || []).forEach(r => {
        if (r.ticketid && !ticketMap[r.ticketid]) {
          const proj = projetoMap[r.id_projeto] || {};
          ticketMap[r.ticketid] = {
            id:                   r.ticketid,
            subject:              r.subject || `Ticket #${r.ticketid}`,
            descricao_customizada: r.descricao_customizada || null,
            ticket_status:        r.ticket_status,
            desenvolvedor:        r.desenvolvedor,
            projeto:              r.descricao,
            id_projeto:           r.id_projeto,
            id_projeto_pai:       proj.id_projeto_pai || null,
          };
        }
      });
      setTickets(Object.values(ticketMap));
      setTarefas(allTarefas || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      isFirst.current = false;
    }

    getGtdTickets()
      .then(data => {
        const map = {};
        (data.tickets || []).forEach(t => { map[t.id_ticket] = t; });
        setGtdMap(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    localStorage.setItem(ACOMP_STORAGE, JSON.stringify({
      status: filtroStatus, projeto: filtroProjeto,
      ticket: filtroTicket, resp: filtroResp,
    }));
  }, [filtroStatus, filtroProjeto, filtroTicket, filtroResp]);

  useEffect(() => {
    const t = setInterval(() => {
      cntRef.current -= 1;
      setCountdown(cntRef.current);
      if (cntRef.current <= 0) { cntRef.current = 60; setCountdown(60); load(true); }
    }, 1000);
    return () => clearInterval(t);
  }, [load]);

  function refresh() { cntRef.current = 60; setCountdown(60); load(true); }

  /* ── Filtros ── */
  const ticketMap = {};
  tickets.forEach(t => { ticketMap[t.id] = t; });

  function handleSelectProjeto(value) { setFiltroProjeto(value); setFiltroTicket(""); }

  const ticketsVisiveis = filtroProjeto
    ? tickets.filter(t => {
        if (filtroProjeto.startsWith("pai:")) {
          const paiId = Number(filtroProjeto.slice(4));
          return t.id_projeto_pai === paiId || t.id_projeto === paiId;
        }
        if (filtroProjeto.startsWith("sub:")) {
          const subId = Number(filtroProjeto.slice(4));
          return t.id_projeto === subId;
        }
        return false;
      })
    : [];

  const filtered = tarefas.filter(t => {
    if (filtroStatus && t.status !== filtroStatus) return false;
    if (filtroResp && !(t.responsavel || "").toLowerCase().includes(filtroResp.toLowerCase())) return false;
    if (filtroTicket && t.id_ticket !== Number(filtroTicket)) return false;
    if (filtroProjeto && !filtroTicket) {
      const tk = ticketMap[t.id_ticket];
      if (!tk) return false;
      if (filtroProjeto.startsWith("pai:")) {
        const paiId = Number(filtroProjeto.slice(4));
        if (tk.id_projeto_pai !== paiId && tk.id_projeto !== paiId) return false;
      } else if (filtroProjeto.startsWith("sub:")) {
        const subId = Number(filtroProjeto.slice(4));
        if (tk.id_projeto !== subId) return false;
      }
    }
    return true;
  });

  /* ── Modal ── */
  function openAdd(id_ticket = "", status = "Nao Iniciada") {
    setForm(emptyForm(String(id_ticket), status));
    setFormErro("");
    setModal({ mode: "add" });
  }

  function openEdit(tarefa) {
    setForm({
      id_ticket:               String(tarefa.id_ticket),
      descritivo:              tarefa.descritivo              || "",
      responsavel:             tarefa.responsavel             || "",
      status:                  tarefa.status                  || "Nao Iniciada",
      data_entrega:            tarefa.data_entrega            ? tarefa.data_entrega.slice(0, 10)            : "",
      data_entrega_real:       tarefa.data_entrega_real       ? tarefa.data_entrega_real.slice(0, 10)       : "",
      descritivo_restricao:    tarefa.descritivo_restricao    || "",
      responsavel_restricao:   tarefa.responsavel_restricao   || "",
      data_liberacao_restricao: tarefa.data_liberacao_restricao ? tarefa.data_liberacao_restricao.slice(0, 10) : "",
    });
    setFormErro("");
    setModal({ mode: "edit", tarefa });
  }

  async function handleSave() {
    if (!form.descritivo.trim()) { setFormErro("Descritivo é obrigatório."); return; }
    if (!form.id_ticket)         { setFormErro("Selecione um ticket.");      return; }
    setSaving(true); setFormErro("");
    try {
      const isBloq = form.status === "Bloqueada";
      const payload = {
        id_ticket:               Number(form.id_ticket),
        descritivo:              form.descritivo.trim(),
        responsavel:             form.responsavel.trim()  || null,
        status:                  form.status,
        data_entrega:            form.data_entrega             || null,
        data_entrega_real:       form.data_entrega_real        || null,
        descritivo_restricao:    isBloq ? (form.descritivo_restricao.trim()    || null) : null,
        responsavel_restricao:   isBloq ? (form.responsavel_restricao.trim()   || null) : null,
        data_liberacao_restricao: isBloq ? (form.data_liberacao_restricao      || null) : null,
      };
      if (modal.mode === "add") await criarTarefa(payload);
      else await atualizarTarefa(modal.tarefa.id_tarefa, payload);
      setModal(null);
      await load();
    } catch (e) {
      setFormErro(e.response?.data?.message || "Erro ao salvar. Tente novamente.");
    } finally { setSaving(false); }
  }

  async function handleDelete(tarefa) {
    if (!window.confirm(`Remover a tarefa "${tarefa.descritivo}"?`)) return;
    try {
      await deletarTarefa(tarefa.id_tarefa);
      setTarefas(prev => prev.filter(t => t.id_tarefa !== tarefa.id_tarefa));
    } catch { alert("Erro ao remover tarefa."); }
  }

  async function handleStatusChange(tarefa, novoStatus) {
    try {
      await atualizarTarefa(tarefa.id_tarefa, { ...tarefa, status: novoStatus });
      setTarefas(prev => prev.map(t =>
        t.id_tarefa === tarefa.id_tarefa ? { ...t, status: novoStatus } : t
      ));
    } catch { alert("Erro ao atualizar status."); }
  }

  async function handleDrop(e, novoStatus) {
    e.preventDefault();
    setDragOverCol(null);
    const id = Number(e.dataTransfer.getData("id_tarefa"));
    const tarefa = tarefas.find(t => t.id_tarefa === id);
    if (!tarefa || tarefa.status === novoStatus) return;
    await handleStatusChange(tarefa, novoStatus);
  }

  function sortCol(list) {
    if (!ordenacao) return list;
    const hoje = new Date(new Date().toDateString());
    return [...list].sort((a, b) => {
      if (ordenacao === "prazo") {
        const aAtras = a.data_entrega && new Date(a.data_entrega) < hoje;
        const bAtras = b.data_entrega && new Date(b.data_entrega) < hoje;
        if (aAtras && !bAtras) return -1;
        if (!aAtras && bAtras) return 1;
        if (!a.data_entrega) return 1;
        if (!b.data_entrega) return -1;
        return new Date(a.data_entrega) - new Date(b.data_entrega);
      }
      if (ordenacao === "projeto") {
        const pa = ticketMap[a.id_ticket]?.projeto || "";
        const pb = ticketMap[b.id_ticket]?.projeto || "";
        return pa.localeCompare(pb);
      }
      return 0;
    });
  }

  const bloqueadas        = filtered.filter(t => t.status === "Bloqueada").length;
  const ticketsFiltrados  = new Set(filtered.map(t => t.id_ticket)).size;
  const projetosFiltrados = new Set(filtered.map(t => ticketMap[t.id_ticket]?.id_projeto).filter(Boolean)).size;

  if (loading && isFirst.current) {
    return (
      <div style={skeletonPage}>
        <div style={skeletonText}>Carregando tarefas...</div>
      </div>
    );
  }

  return (
    <div style={pageWrapper}>

      {/* ── Painel esquerdo ── */}
      <ProjectPanel
        hierarquia={hierarquia}
        tarefas={tarefas}
        ticketMap={ticketMap}
        filtroProjeto={filtroProjeto}
        onSelect={handleSelectProjeto}
        aberto={painelAberto}
        onToggle={() => setPainelAberto(v => !v)}
      />

      {/* ── Conteúdo principal ── */}
      <div style={page}>

        {/* Header */}
        <div style={headerRow}>
          <div>
            <h1 style={pageTitle}>Acompanhamento de Tarefas</h1>
            <div style={pageSub}>
              {projetosFiltrados} projeto{projetosFiltrados !== 1 ? "s" : ""}
              {" · "}
              {ticketsFiltrados} ticket{ticketsFiltrados !== 1 ? "s" : ""}
              {" · "}
              {filtered.length} tarefa{filtered.length !== 1 ? "s" : ""}
              {bloqueadas > 0 && (
                <span style={alertBadge}>⚠ {bloqueadas} bloqueada{bloqueadas > 1 ? "s" : ""}</span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button style={refreshBtn} onClick={refresh}>↻ {countdown}s</button>
            <button style={addBtn} onClick={() => openAdd(filtroTicket)}>+ Nova Tarefa</button>
          </div>
        </div>

        {/* Filtros */}
        <div style={filterRow}>
          {/* Chips de status — compactos */}
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            <button
              style={filtroStatus === null ? chipSel("#60a5fa") : chipDef}
              onClick={() => setFiltroStatus(null)}
            >
              Todos ({tarefas.length})
            </button>
            {COLUNAS.map(c => {
              const count = tarefas.filter(t => t.status === c.key).length;
              return (
                <button
                  key={c.key}
                  style={filtroStatus === c.key ? chipSel(c.cor) : chipDef}
                  onClick={() => setFiltroStatus(filtroStatus === c.key ? null : c.key)}
                >
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: filtroStatus === c.key ? c.cor : "#475569", display: "inline-block", marginRight: 4, verticalAlign: "middle" }} />
                  {c.label} ({count})
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {ticketsVisiveis.length > 0 && (
              <select
                style={{ ...searchInp, width: 220, cursor: "pointer" }}
                value={filtroTicket}
                onChange={e => setFiltroTicket(e.target.value)}
              >
                <option value="">Todos os tickets ({ticketsVisiveis.length})</option>
                {ticketsVisiveis.map(t => (
                  <option key={t.id} value={t.id}>
                    #{t.id} — {t.subject?.slice(0, 38)}{t.subject?.length > 38 ? "…" : ""}
                  </option>
                ))}
              </select>
            )}
            <select
              style={{ ...searchInp, width: 140, cursor: "pointer" }}
              value={ordenacao}
              onChange={e => { setOrdenacao(e.target.value); localStorage.setItem("tarefas_acomp_ordem", e.target.value); }}
            >
              <option value="">Ordenação padrão</option>
              <option value="prazo">Por prazo</option>
              <option value="projeto">Por projeto</option>
            </select>
            <input
              style={{ ...searchInp, width: 160 }}
              placeholder="Responsável..."
              value={filtroResp}
              onChange={e => setFiltroResp(e.target.value)}
            />
          </div>
        </div>

        {/* Kanban */}
        <div style={board}>
          {COLUNAS.map(col => {
            const colTarefas = sortCol(filtered.filter(t => t.status === col.key));
            const isDragOver = dragOverCol === col.key;
            const temItens   = colTarefas.length > 0;
            const isBloqCol  = col.key === "Bloqueada" && temItens;

            return (
              <div
                key={col.key}
                style={{
                  ...column,
                  boxShadow: isBloqCol
                    ? `0 0 0 1px rgba(248,113,113,0.25), 0 0 24px rgba(248,113,113,0.06)`
                    : "none",
                }}
                onDragOver={e => { e.preventDefault(); setDragOverCol(col.key); }}
                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                onDrop={e => handleDrop(e, col.key)}
              >
                {/* Header da coluna — acento colorido */}
                <div style={colHead(col.cor, isDragOver)}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.cor, boxShadow: `0 0 6px ${col.cor}88`, flexShrink: 0 }} />
                  <span style={{ ...colHeadLabel, color: col.cor }}>{col.label}</span>
                  <span style={colBadge(col.cor)}>{colTarefas.length}</span>
                </div>

                {/* Cards */}
                <div style={colBody}>
                  {colTarefas.map(t => (
                    <TarefaCard
                      key={t.id_tarefa}
                      tarefa={t}
                      ticket={ticketMap[t.id_ticket] || {}}
                      commentCount={t.comentario_count || 0}
                      onEdit={() => openEdit(t)}
                      onDelete={() => handleDelete(t)}
                      onStatusChange={s => handleStatusChange(t, s)}
                      gtdRegistro={gtdMap[t.id_ticket]}
                      onGtdChanged={registro => setGtdMap(prev => {
                        const next = { ...prev };
                        if (registro) next[t.id_ticket] = registro;
                        else delete next[t.id_ticket];
                        return next;
                      })}
                    />
                  ))}

                  {colTarefas.length === 0 && (
                    <div style={isDragOver ? emptyMsgDrop : emptyMsg}>
                      {isDragOver ? "↓ Soltar aqui" : "Nenhuma tarefa"}
                    </div>
                  )}
                </div>

                {/* + Adicionar — logo após os cards */}
                <ColAddButton onClick={() => openAdd(filtroTicket, col.key)} cor={col.cor} />
              </div>
            );
          })}
        </div>

      </div>

      {/* Modal */}
      {modal && (
        <TarefaModal
          mode={modal.mode}
          idTarefa={modal.tarefa?.id_tarefa}
          onComentariosChanged={(id_tarefa, novoCount) =>
            setTarefas(prev => prev.map(t =>
              t.id_tarefa === id_tarefa ? { ...t, comentario_count: novoCount } : t
            ))
          }
          form={form}
          setForm={setForm}
          tickets={tickets}
          responsaveis={[
            ...new Set([
              ...tarefas.map(t => t.responsavel).filter(Boolean),
              ...tickets.map(t => t.desenvolvedor).filter(Boolean),
            ])
          ].sort()}
          erro={formErro}
          saving={saving}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

    </div>
  );
}

/* ─── ColAddButton ───────────────────────────────────────────────── */

function ColAddButton({ onClick, cor }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      style={{
        margin: "0 10px 10px",
        padding: "7px 12px",
        borderRadius: 8,
        border: `1.5px dashed ${hovered ? cor + "60" : "rgba(96,165,250,0.15)"}`,
        background: hovered ? `${cor}08` : "transparent",
        color: hovered ? cor : "#334155",
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 5,
        transition: "all 0.15s",
        width: "calc(100% - 20px)",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ fontSize: 14, lineHeight: 1, fontWeight: 400 }}>+</span>
      Adicionar tarefa
    </button>
  );
}

/* ─── ProjectPanel ───────────────────────────────────────────────── */

function saudeProjeto(tarefasDoProjeto) {
  const ativas = tarefasDoProjeto.filter(t => t.status !== "Concluida");
  if (ativas.length === 0 && tarefasDoProjeto.length > 0)
    return { cor: "#4ade80", title: "Todas concluídas" };
  const hoje = new Date(new Date().toDateString());
  const temAtrasada  = ativas.some(t => t.data_entrega && new Date(t.data_entrega) < hoje);
  const temBloqueada = ativas.some(t => t.status === "Bloqueada");
  const temVencendo  = ativas.some(t => {
    if (!t.data_entrega) return false;
    const diff = Math.floor((new Date(t.data_entrega) - hoje) / 86400000);
    return diff >= 0 && diff <= 2;
  });
  if (temAtrasada)  return { cor: "#f87171", title: "Tem tarefas atrasadas" };
  if (temBloqueada) return { cor: "#fb923c", title: "Tem tarefas bloqueadas" };
  if (temVencendo)  return { cor: "#fbbf24", title: "Tem tarefas vencendo em breve" };
  return { cor: "#4ade80", title: "Em dia" };
}

function ProjectPanel({ hierarquia, tarefas, ticketMap, filtroProjeto, onSelect, aberto, onToggle }) {
  const countPorProjeto = {};
  const tarefasPorProjeto = {};
  tarefas.forEach(t => {
    const tk = ticketMap[t.id_ticket];
    if (!tk) return;
    countPorProjeto[tk.id_projeto] = (countPorProjeto[tk.id_projeto] || 0) + 1;
    if (!tarefasPorProjeto[tk.id_projeto]) tarefasPorProjeto[tk.id_projeto] = [];
    tarefasPorProjeto[tk.id_projeto].push(t);
    if (tk.id_projeto_pai) {
      const k = `pai_${tk.id_projeto_pai}`;
      countPorProjeto[k] = (countPorProjeto[k] || 0) + 1;
      if (!tarefasPorProjeto[k]) tarefasPorProjeto[k] = [];
      tarefasPorProjeto[k].push(t);
    }
  });

  return (
    <div style={panel(aberto)}>
      <button style={panelToggleBtn} onClick={onToggle} title={aberto ? "Recolher painel" : "Expandir painel"}>
        {aberto ? "◀" : "▶"}
      </button>

      {aberto && (
        <div style={panelBody}>
          <div style={panelTitle}>PROJETOS</div>

          <div style={panelItem(!filtroProjeto, false)} onClick={() => onSelect("")}>
            <span style={panelItemIcon}>◈</span>
            <span style={panelItemLabel}>Todas as tarefas</span>
            <span style={panelItemBadge("#60a5fa")}>{tarefas.length}</span>
          </div>

          {hierarquia.map(pai => {
            const paiCount = countPorProjeto[`pai_${pai.id}`] || 0;
            const isPaiSel = filtroProjeto === `pai:${pai.id}`;
            const paiSaude = saudeProjeto(tarefasPorProjeto[`pai_${pai.id}`] || []);
            return (
              <div key={pai.id}>
                <div style={panelItem(isPaiSel, false)} onClick={() => onSelect(`pai:${pai.id}`)}>
                  <span style={saudeDot(paiSaude.cor)} title={paiSaude.title} />
                  <span style={panelItemIcon}>📁</span>
                  <span style={panelItemLabel}>{pai.descricao}</span>
                  {paiCount > 0 && <span style={panelItemBadge("#22d3ee")}>{paiCount}</span>}
                </div>
                {pai.filhos.map(filho => {
                  const filhoCount = countPorProjeto[filho.id] || 0;
                  const isFilhoSel = filtroProjeto === `sub:${filho.id}`;
                  const filhoSaude = saudeProjeto(tarefasPorProjeto[filho.id] || []);
                  return (
                    <div key={filho.id} style={panelItem(isFilhoSel, true)} onClick={() => onSelect(`sub:${filho.id}`)}>
                      <span style={panelConnector}>└</span>
                      <span style={saudeDot(filhoSaude.cor)} title={filhoSaude.title} />
                      <span style={{ ...panelItemLabel, fontSize: 12 }}>{filho.descricao}</span>
                      {filhoCount > 0 && <span style={panelItemBadge("#94a3b8")}>{filhoCount}</span>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── TarefaCard ─────────────────────────────────────────────────── */

function TarefaCard({ tarefa, ticket, commentCount, onEdit, onDelete, onStatusChange, gtdRegistro, onGtdChanged }) {
  const [hovered, setHovered] = useState(false);
  const overdue  = isOverdue(tarefa.data_entrega, tarefa.status);
  const acoes    = PROXIMAS_ACOES[tarefa.status] || [];
  const isBloq   = tarefa.status === "Bloqueada";
  const urgCor   = urgenciaCor(tarefa.data_entrega, tarefa.status);
  const urgLabel = urgenciaLabel(tarefa.data_entrega, tarefa.status);

  return (
    <div
      style={{
        ...card,
        borderLeft: `3px solid ${urgCor}`,
        boxShadow: hovered ? `0 4px 16px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(96,165,250,0.12)` : "none",
      }}
      draggable
      onDragStart={e => {
        e.dataTransfer.setData("id_tarefa", tarefa.id_tarefa);
        e.dataTransfer.effectAllowed = "move";
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >

      {/* Linha superior: projeto + ticket + grip */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
        <div style={cardContext}>
          {ticket.projeto && <div style={projetoTag}>📁 {ticket.projeto}</div>}
          {(() => {
            const desc = ticket.descricao_customizada || ticket.subject || "";
            const label = desc ? `#${tarefa.id_ticket} · ${desc}` : `#${tarefa.id_ticket}`;
            return <div style={ticketTag} title={label}>🎫 {label}</div>;
          })()}
        </div>
        {/* Atribuição rápida ao quadro GTD */}
        <div style={{ marginLeft: "auto", flexShrink: 0 }} draggable={false} onMouseDown={e => e.stopPropagation()}>
          <GtdQuickAssign
            idTicket={tarefa.id_ticket}
            categoriaAtual={gtdRegistro?.categoria}
            observacaoAtual={gtdRegistro?.observacao}
            onChanged={onGtdChanged}
          />
        </div>
        {/* Grip de drag-and-drop */}
        <div style={{ color: "#334155", fontSize: 16, paddingTop: 2, opacity: hovered ? 0.6 : 0.2, transition: "opacity 0.15s", userSelect: "none", cursor: "grab", flexShrink: 0 }}>
          ⠿
        </div>
      </div>

      {/* Descritivo */}
      <div style={cardDesc}>{tarefa.descritivo}</div>

      {/* Responsável com avatar */}
      {tarefa.responsavel && (
        <div style={cardMeta}>
          <Avatar nome={tarefa.responsavel} />
          <span style={{ color: "#94a3b8", fontSize: 11 }}>{tarefa.responsavel}</span>
        </div>
      )}

      {/* Data de entrega + label de urgência */}
      {tarefa.data_entrega && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <div style={{ ...cardMeta, color: overdue ? "#f87171" : "#94a3b8" }}>
            <span>{overdue ? "⚠" : "📅"}</span>
            <span>{fmtDate(tarefa.data_entrega)}</span>
          </div>
          {urgLabel && (
            <span style={{
              fontSize: 9, fontWeight: 700,
              color: urgCor === "rgba(96,165,250,0.2)" ? "#60a5fa" : urgCor,
              background: urgCor === "rgba(96,165,250,0.2)" ? "rgba(96,165,250,0.08)" : `${urgCor}18`,
              border: `1px solid ${urgCor === "rgba(96,165,250,0.2)" ? "rgba(96,165,250,0.2)" : urgCor + "44"}`,
              borderRadius: 5, padding: "1px 6px",
            }}>
              {urgLabel}
            </span>
          )}
        </div>
      )}

      {/* Data entregue real */}
      {tarefa.data_entrega_real && (
        <div style={{ ...cardMeta, color: "#4ade80" }}>
          <span>✅</span>
          <span>Entregue em {fmtDate(tarefa.data_entrega_real)}</span>
        </div>
      )}

      {/* Bloqueio */}
      {isBloq && (tarefa.descritivo_restricao || tarefa.responsavel_restricao) && (
        <div style={restricaoBox}>
          <div style={restricaoTitle}>⛔ Bloqueio</div>
          {tarefa.descritivo_restricao  && <div style={restricaoDesc}>{tarefa.descritivo_restricao}</div>}
          {tarefa.responsavel_restricao && <div style={restricaoMeta}>Com: {tarefa.responsavel_restricao}</div>}
          {tarefa.data_liberacao_restricao && (
            <div style={restricaoMeta}>Previsão de liberação: {fmtDate(tarefa.data_liberacao_restricao)}</div>
          )}
        </div>
      )}

      {/* Badge comentários */}
      {commentCount > 0 && (
        <div style={commentBadgeStyle}>💬 {commentCount} comentário{commentCount > 1 ? "s" : ""}</div>
      )}

      {/* Ações */}
      <div style={cardActions}>
        <div style={{ display: "flex", gap: 4, flex: 1, flexWrap: "wrap" }}>
          {acoes.map(a => (
            <button key={a.key} style={actionBtn(a.cor)} onClick={() => onStatusChange(a.key)}>
              → {a.label}
            </button>
          ))}
        </div>
        <button style={iconBtn("#60a5fa")} onClick={onEdit} title="Editar">✎</button>
        {/* ✕ visível só no hover */}
        <button
          style={{ ...iconBtn("#f87171"), opacity: hovered ? 1 : 0, transition: "opacity 0.18s" }}
          onClick={onDelete}
          title="Remover"
          tabIndex={hovered ? 0 : -1}
        >
          ✕
        </button>
      </div>

    </div>
  );
}

/* ─── TarefaModal ─────────────────────────────────────────────────── */

function TarefaModal({ mode, idTarefa, form, setForm, tickets, responsaveis, erro, saving, onSave, onClose, onComentariosChanged }) {
  const set    = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isBloq = form.status === "Bloqueada";
  const user   = getUser();
  const autor  = user?.name || user?.login || "Usuário";

  const [comentarios,    setComentarios]    = useState([]);
  const [novoComentario, setNovoComentario] = useState("");
  const [savingComent,   setSavingComent]   = useState(false);
  const [historico,      setHistorico]      = useState([]);
  const [verHistorico,   setVerHistorico]   = useState(false);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  useEffect(() => {
    if (mode === "edit" && idTarefa) {
      getComentarios(idTarefa)
        .then(data => { setComentarios(data); onComentariosChanged?.(idTarefa, data.length); })
        .catch(() => setComentarios([]));
      getHistorico(idTarefa)
        .then(setHistorico)
        .catch(() => setHistorico([]));
    }
  }, [mode, idTarefa]); // eslint-disable-line

  async function handleAddComentario() {
    if (!novoComentario.trim()) return;
    setSavingComent(true);
    try {
      const novo = await adicionarComentario(idTarefa, autor, novoComentario.trim());
      const updated = [...comentarios, novo];
      setComentarios(updated);
      setNovoComentario("");
      onComentariosChanged?.(idTarefa, updated.length);
    } catch { /* silencioso */ }
    finally { setSavingComent(false); }
  }

  async function handleDeleteComentario(idComent) {
    try {
      await deletarComentario(idTarefa, idComent);
      const updated = comentarios.filter(c => c.id_comentario !== idComent);
      setComentarios(updated);
      onComentariosChanged?.(idTarefa, updated.length);
    } catch { /* silencioso */ }
  }

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modalBox}>

        <div style={modalHeader}>
          <span style={modalTitle}>{mode === "add" ? "Nova Tarefa" : "Editar Tarefa"}</span>
          <button style={modalCloseBtn} onClick={onClose}>✕</button>
        </div>

        <div style={modalBody}>

          <div>
            <label style={fieldLabel}>Ticket *</label>
            <select style={fieldSelect} value={form.id_ticket} onChange={e => set("id_ticket", e.target.value)}>
              <option value="">Selecione um ticket...</option>
              {tickets.map(t => (
                <option key={t.id} value={t.id}>#{t.id} — {t.subject}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={fieldLabel}>Descritivo *</label>
            <textarea
              style={{ ...fieldInput, height: 72, resize: "vertical" }}
              value={form.descritivo}
              onChange={e => set("descritivo", e.target.value)}
              placeholder="Descreva a tarefa..."
            />
          </div>

          <div style={twoCol}>
            <div>
              <label style={fieldLabel}>Responsável</label>
              <input
                style={fieldInput}
                list="lista-responsaveis"
                value={form.responsavel}
                onChange={e => set("responsavel", e.target.value)}
                placeholder="Escolha ou digite..."
                autoComplete="off"
              />
              <datalist id="lista-responsaveis">
                {responsaveis.map(r => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div>
              <label style={fieldLabel}>Status</label>
              <select style={fieldSelect} value={form.status} onChange={e => set("status", e.target.value)}>
                <option value="Nao Iniciada">Não Iniciada</option>
                <option value="Em Andamento">Em Andamento</option>
                <option value="Bloqueada">Bloqueada</option>
                <option value="Concluida">Concluída</option>
              </select>
            </div>
          </div>

          <div style={twoCol}>
            <div>
              <label style={fieldLabel}>Prazo (data limite)</label>
              <input type="date" style={fieldInput} value={form.data_entrega} onChange={e => set("data_entrega", e.target.value)} />
            </div>
            <div>
              <label style={fieldLabel}>Concluída em (data real)</label>
              <input type="date" style={fieldInput} value={form.data_entrega_real} onChange={e => set("data_entrega_real", e.target.value)} />
            </div>
          </div>

          {isBloq && (
            <div style={restricaoSection}>
              <div style={restricaoSectionTitle}>⛔ Detalhes do Bloqueio</div>
              <div>
                <label style={fieldLabel}>Descrição do Bloqueio</label>
                <textarea
                  style={{ ...fieldInput, height: 60, resize: "vertical" }}
                  value={form.descritivo_restricao}
                  onChange={e => set("descritivo_restricao", e.target.value)}
                  placeholder="Qual é a restrição?"
                />
              </div>
              <div style={twoCol}>
                <div>
                  <label style={fieldLabel}>Com quem está</label>
                  <input
                    style={fieldInput}
                    list="lista-responsaveis"
                    value={form.responsavel_restricao}
                    onChange={e => set("responsavel_restricao", e.target.value)}
                    placeholder="Escolha ou digite..."
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label style={fieldLabel}>Previsão de Liberação</label>
                  <input type="date" style={fieldInput} value={form.data_liberacao_restricao} onChange={e => set("data_liberacao_restricao", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {mode === "edit" && (
            <div style={comentariosSection}>
              <div style={comentariosTitle}>
                💬 Comentários {comentarios.length > 0 && <span style={comentBadge}>{comentarios.length}</span>}
              </div>
              {comentarios.length === 0 && (
                <div style={{ color: "#334155", fontSize: 11, fontStyle: "italic" }}>Nenhum comentário ainda.</div>
              )}
              {comentarios.map(c => (
                <div key={c.id_comentario} style={comentItem}>
                  <div style={comentHeader}>
                    <span style={comentAutor}>{c.autor}</span>
                    <span style={comentData}>{new Date(c.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    {c.autor === autor && (
                      <button style={comentDelBtn} onClick={() => handleDeleteComentario(c.id_comentario)} title="Remover">✕</button>
                    )}
                  </div>
                  <div style={comentTexto}>{c.comentario}</div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <input
                  style={{ ...fieldInput, flex: 1 }}
                  placeholder="Adicionar comentário..."
                  value={novoComentario}
                  onChange={e => setNovoComentario(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddComentario(); } }}
                />
                <button
                  style={{ ...btnSave, padding: "8px 14px", opacity: savingComent ? 0.6 : 1 }}
                  onClick={handleAddComentario}
                  disabled={savingComent || !novoComentario.trim()}
                >
                  {savingComent ? "..." : "Enviar"}
                </button>
              </div>
            </div>
          )}

          {mode === "edit" && (
            <div style={historicoSection}>
              <button style={historicoToggle} onClick={() => setVerHistorico(v => !v)}>
                🕐 Histórico de alterações ({historico.length})
                <span style={{ marginLeft: 6 }}>{verHistorico ? "▲" : "▼"}</span>
              </button>
              {verHistorico && historico.length === 0 && (
                <div style={{ color: "#334155", fontSize: 11, fontStyle: "italic", marginTop: 8, paddingLeft: 12 }}>
                  Nenhuma alteração registrada ainda.
                </div>
              )}
              {verHistorico && historico.length > 0 && (
                <div style={historicoLista}>
                  {historico.map(h => (
                    <div key={h.id_historico} style={historicoItem}>
                      <div style={historicoLine} />
                      <div style={{ flex: 1 }}>
                        <div style={historicoMeta}>
                          <span style={historicoCampo}>{h.campo}</span>
                          <span style={{ color: "#334155" }}>·</span>
                          <span style={{ color: "#475569" }}>{h.autor || "Sistema"}</span>
                          <span style={{ color: "#334155", marginLeft: "auto" }}>
                            {new Date(h.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        <div style={historicoValores}>
                          {h.valor_anterior && <span style={valAnterior}>{h.valor_anterior}</span>}
                          {h.valor_anterior && <span style={{ color: "#334155" }}>→</span>}
                          <span style={valNovo}>{h.valor_novo}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {erro && <div style={erroBox}>{erro}</div>}
        </div>

        <div style={modalFooter}>
          <button style={btnCancel} onClick={onClose}>Cancelar</button>
          <button style={btnSave} onClick={onSave} disabled={saving}>
            {saving ? "Salvando..." : mode === "add" ? "Criar Tarefa" : "Salvar Alterações"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const pageWrapper = { display: "flex", height: "100vh", overflow: "hidden" };

const page = {
  flex: 1, padding: "28px 28px 28px 24px",
  display: "flex", flexDirection: "column", gap: 16,
  overflow: "auto",
};

const panel = aberto => ({
  width: aberto ? 220 : 32, minWidth: aberto ? 220 : 32,
  background: "linear-gradient(180deg, #08122a 0%, #060d1e 100%)",
  borderRight: "1px solid rgba(96,165,250,0.08)",
  display: "flex", flexDirection: "column",
  transition: "width 0.22s ease, min-width 0.22s ease",
  overflow: "hidden", flexShrink: 0,
});

const panelToggleBtn = {
  alignSelf: "flex-end", margin: "14px 6px 0",
  width: 22, height: 22, borderRadius: 6,
  border: "1px solid rgba(96,165,250,0.15)", background: "rgba(96,165,250,0.06)",
  color: "#475569", fontSize: 10, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
};

const panelBody = {
  display: "flex", flexDirection: "column", gap: 2,
  padding: "10px 8px 20px", overflowY: "auto", flex: 1,
};

const panelTitle = {
  fontSize: 9, fontWeight: 700, color: "#334155",
  textTransform: "uppercase", letterSpacing: 1,
  padding: "6px 8px 8px", whiteSpace: "nowrap",
};

const panelItem = (ativo, indentado) => ({
  display: "flex", alignItems: "center", gap: 6,
  padding: indentado ? "5px 8px 5px 18px" : "6px 8px",
  borderRadius: 8, cursor: "pointer",
  background: ativo ? "linear-gradient(90deg,rgba(59,130,246,0.18),rgba(6,182,212,0.08))" : "transparent",
  boxShadow: ativo ? "inset 0 0 0 1px rgba(59,130,246,0.22)" : "none",
  transition: "background 0.12s", whiteSpace: "nowrap", overflow: "hidden",
});

const saudeDot = cor => ({
  width: 7, height: 7, borderRadius: "50%", background: cor,
  flexShrink: 0, boxShadow: `0 0 4px ${cor}88`,
});

const panelItemIcon  = { fontSize: 13, flexShrink: 0 };
const panelConnector = { fontSize: 11, color: "#334155", flexShrink: 0 };
const panelItemLabel = { fontSize: 13, color: "#94a3b8", flex: 1, overflow: "hidden", textOverflow: "ellipsis" };

const panelItemBadge = cor => ({
  fontSize: 10, fontWeight: 700, color: cor,
  background: `${cor}22`, border: `1px solid ${cor}44`,
  borderRadius: 8, padding: "1px 6px", flexShrink: 0,
});

const headerRow = {
  display: "flex", justifyContent: "space-between",
  alignItems: "flex-start", flexWrap: "wrap", gap: 12,
};

const pageTitle = { color: "#f1f5f9", fontSize: 22, fontWeight: 700, margin: 0 };
const pageSub   = { color: "#475569", fontSize: 13, marginTop: 4, display: "flex", alignItems: "center", gap: 10 };

const alertBadge = {
  background: "rgba(248,113,113,0.15)", color: "#f87171",
  border: "1px solid rgba(248,113,113,0.3)",
  borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700,
};

const refreshBtn = {
  padding: "8px 14px", borderRadius: 9,
  border: "1px solid rgba(96,165,250,0.2)", background: "rgba(96,165,250,0.05)",
  color: "#60a5fa", fontSize: 12, fontWeight: 600, cursor: "pointer",
};

const addBtn = {
  padding: "9px 18px", borderRadius: 9, border: "none",
  background: "linear-gradient(135deg, #3b82f6, #06b6d4)",
  color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
};

const filterRow = {
  display: "flex", justifyContent: "space-between",
  alignItems: "center", flexWrap: "wrap", gap: 8,
};

/* Chips de status — mais compactos */
const chipDef = {
  padding: "4px 10px", borderRadius: 20,
  border: "1px solid rgba(96,165,250,0.1)",
  background: "rgba(11,22,42,0.5)",
  color: "#475569", fontSize: 10, fontWeight: 600, cursor: "pointer",
  display: "flex", alignItems: "center",
};

const chipSel = cor => ({
  ...chipDef,
  background: `${cor}18`, border: `1px solid ${cor}44`, color: cor,
});

const searchInp = {
  padding: "7px 12px", borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.15)",
  background: "rgba(11,22,42,0.6)",
  color: "#e2e8f0", fontSize: 12, outline: "none",
};

const board = {
  display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
  gap: 14, alignItems: "start",
};

const column = {
  background: "rgba(11,22,42,0.5)",
  border: "1px solid rgba(96,165,250,0.08)",
  borderRadius: 14,
  display: "flex", flexDirection: "column",
  transition: "box-shadow 0.2s",
};

/* Header da coluna com label colorido */
const colHead = (cor, dragOver) => ({
  padding: "12px 14px",
  display: "flex", alignItems: "center", gap: 8,
  borderBottom: `2px solid ${dragOver ? cor : cor + "40"}`,
  background: dragOver ? `${cor}22` : `${cor}0a`,
  borderRadius: "14px 14px 0 0",
  transition: "background 0.15s",
});

const colHeadLabel = {
  fontSize: 11, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.8,
  flex: 1,
};

const colBadge = cor => ({
  background: `${cor}22`, color: cor,
  border: `1px solid ${cor}44`,
  borderRadius: 10, padding: "1px 8px",
  fontSize: 11, fontWeight: 700,
});

/* Área de cards — sem maxHeight fixo, scroll automático quando necessário */
const colBody = {
  display: "flex", flexDirection: "column", gap: 8,
  padding: "10px 10px 4px",
};

const emptyMsg = {
  color: "#2d3a4a", fontSize: 11,
  textAlign: "center", padding: "16px 0",
  fontStyle: "italic",
};

const emptyMsgDrop = {
  color: "#60a5fa", fontSize: 12, fontWeight: 600,
  textAlign: "center", padding: "16px 0",
  border: "1px dashed rgba(96,165,250,0.4)",
  borderRadius: 8, background: "rgba(96,165,250,0.04)",
};

/* Card com borderLeft gerenciado inline */
const card = {
  background: "rgba(11,22,42,0.72)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 10, padding: "11px 11px 10px",
  display: "flex", flexDirection: "column", gap: 7,
  backdropFilter: "blur(8px)", cursor: "grab",
  transition: "box-shadow 0.15s",
};

const cardContext = { display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 };

const projetoTag = {
  fontSize: 10, fontWeight: 600, color: "#22d3ee",
  background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.18)",
  borderRadius: 4, padding: "2px 6px", lineHeight: 1.6,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

const ticketTag = {
  fontSize: 10, fontWeight: 600, color: "#60a5fa",
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.2)",
  borderRadius: 4, padding: "2px 6px", lineHeight: 1.6, wordBreak: "break-word",
};

const cardDesc = { color: "#e2e8f0", fontSize: 13, fontWeight: 500, lineHeight: 1.4 };
const cardMeta = { display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#94a3b8" };

const restricaoBox = {
  background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)",
  borderRadius: 8, padding: "7px 10px",
  display: "flex", flexDirection: "column", gap: 3,
};

const restricaoTitle = { fontSize: 10, fontWeight: 700, color: "#f87171", textTransform: "uppercase", letterSpacing: 0.5 };
const restricaoDesc  = { fontSize: 11, color: "#fca5a5" };
const restricaoMeta  = { fontSize: 10, color: "#94a3b8" };

const cardActions = {
  display: "flex", alignItems: "center", gap: 4,
  paddingTop: 6, borderTop: "1px solid rgba(96,165,250,0.07)",
};

const actionBtn = cor => ({
  padding: "3px 8px", borderRadius: 6,
  border: `1px solid ${cor}44`, background: `${cor}11`,
  color: cor, fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
});

const iconBtn = cor => ({
  width: 26, height: 26, borderRadius: 7,
  border: `1px solid ${cor}33`, background: "transparent",
  color: cor, fontSize: 12, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
});

const commentBadgeStyle = {
  fontSize: 10, fontWeight: 600, color: "#60a5fa",
  background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)",
  borderRadius: 5, padding: "2px 7px", display: "inline-block",
};

/* Modal */
const backdrop = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000, padding: 16,
};

const modalBox = {
  background: "#08122a", border: "1px solid rgba(96,165,250,0.15)",
  borderRadius: 16, width: "100%", maxWidth: 560,
  display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden",
};

const modalHeader = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  padding: "18px 22px 14px", borderBottom: "1px solid rgba(96,165,250,0.1)",
};

const modalTitle    = { color: "#f1f5f9", fontWeight: 700, fontSize: 16 };
const modalCloseBtn = { background: "transparent", border: "none", color: "#475569", fontSize: 16, cursor: "pointer" };
const modalBody     = { padding: "18px 22px", display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" };
const modalFooter   = { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 22px", borderTop: "1px solid rgba(96,165,250,0.1)" };

const fieldLabel  = { display: "block", color: "#94a3b8", fontSize: 11, fontWeight: 600, marginBottom: 5 };
const fieldInput  = { width: "100%", padding: "8px 11px", borderRadius: 8, border: "1px solid rgba(96,165,250,0.15)", background: "rgba(11,22,42,0.7)", color: "#e2e8f0", fontSize: 13, outline: "none", boxSizing: "border-box", colorScheme: "dark" };
const fieldSelect = { ...fieldInput, cursor: "pointer" };
const twoCol      = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };

const restricaoSection      = { background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 10, padding: "14px", display: "flex", flexDirection: "column", gap: 10 };
const restricaoSectionTitle = { color: "#f87171", fontSize: 12, fontWeight: 700 };

const comentariosSection = { borderTop: "1px solid rgba(96,165,250,0.1)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 };
const comentariosTitle   = { fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: 0.8, display: "flex", alignItems: "center", gap: 6 };
const comentBadge        = { background: "rgba(96,165,250,0.15)", color: "#60a5fa", borderRadius: 8, padding: "0px 6px", fontSize: 10 };
const comentItem         = { background: "rgba(96,165,250,0.05)", border: "1px solid rgba(96,165,250,0.1)", borderRadius: 8, padding: "8px 10px" };
const comentHeader       = { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 };
const comentAutor        = { fontSize: 11, fontWeight: 700, color: "#60a5fa" };
const comentData         = { fontSize: 10, color: "#334155", flex: 1 };
const comentDelBtn       = { background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 11, padding: "0 2px", lineHeight: 1 };
const comentTexto        = { fontSize: 12, color: "#94a3b8", lineHeight: 1.5 };

const historicoSection = { borderTop: "1px solid rgba(96,165,250,0.08)", paddingTop: 10 };
const historicoToggle  = { background: "none", border: "none", cursor: "pointer", color: "#475569", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, padding: 0, width: "100%" };
const historicoLista   = { display: "flex", flexDirection: "column", gap: 8, marginTop: 10 };
const historicoItem    = { display: "flex", gap: 10, alignItems: "flex-start" };
const historicoLine    = { width: 2, minHeight: 36, background: "rgba(96,165,250,0.15)", borderRadius: 2, flexShrink: 0, marginTop: 4 };
const historicoMeta    = { display: "flex", alignItems: "center", gap: 6, fontSize: 10, marginBottom: 3 };
const historicoCampo   = { fontSize: 10, fontWeight: 700, color: "#60a5fa", background: "rgba(96,165,250,0.1)", borderRadius: 4, padding: "1px 5px" };
const historicoValores = { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" };
const valAnterior      = { fontSize: 11, color: "#475569", textDecoration: "line-through" };
const valNovo          = { fontSize: 11, color: "#e2e8f0", fontWeight: 500 };

const erroBox   = { color: "#f87171", fontSize: 12, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 8, padding: "8px 12px" };
const btnCancel = { padding: "9px 18px", borderRadius: 9, border: "1px solid rgba(96,165,250,0.15)", background: "transparent", color: "#475569", fontSize: 13, cursor: "pointer" };
const btnSave   = { padding: "9px 20px", borderRadius: 9, border: "none", background: "linear-gradient(135deg, #3b82f6, #06b6d4)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" };

const skeletonPage = { display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" };
const skeletonText = { color: "#475569", fontSize: 14 };
